import { useEffect, useMemo, useState } from 'react'
import Button from '../components/ui/Button'
import ConfirmDialog from '../components/ui/ConfirmDialog'
import DataTable from '../components/ui/DataTable'
import EmptyState from '../components/ui/EmptyState'
import ErrorState from '../components/ui/ErrorState'
import Input from '../components/ui/Input'
import LoadingState from '../components/ui/LoadingState'
import PageHeader from '../components/ui/PageHeader'
import PageShell from '../components/ui/PageShell'
import SectionCard from '../components/ui/SectionCard'
import { ui } from '../styles/ui'
import { formatDateTime } from '../utils/date'
import { formatEmptyValue } from '../utils/displayValue'
import { formatSubjectDisplay, formatUsageSummary, BUCKET_TYPE_OPTIONS, ENDPOINT_GROUP_OPTIONS, SUBJECT_TYPE_OPTIONS } from './public-api-access/publicApiAccessPageUtils'
import { clearPublicApiUsage, deletePublicApiUsageBucket, listPublicApiUsage } from '../services/publicApiAccessAdminService'

const initialFilters = {
  subjectType: '',
  subjectId: '',
  endpointGroup: '',
  bucketType: '',
  from: '',
  to: '',
}

const emptySummary = {
  requestCount: 0,
  acceptedCount: 0,
  rejectedCount: 0,
  costUnits: 0,
}

function isDateRangeValid(from, to) {
  if (!from || !to) return true
  const fromDate = new Date(from)
  const toDate = new Date(to)
  return !Number.isNaN(fromDate.getTime()) && !Number.isNaN(toDate.getTime()) && toDate >= fromDate
}

export default function PublicApiUsageAdmin() {
  const [filters, setFilters] = useState(initialFilters)
  const [appliedFilters, setAppliedFilters] = useState(initialFilters)
  const [refreshTick, setRefreshTick] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [items, setItems] = useState([])
  const [summary, setSummary] = useState(emptySummary)
  const [formError, setFormError] = useState('')
  const [pendingDeleteBucket, setPendingDeleteBucket] = useState(null)
  const [pendingClearAll, setPendingClearAll] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    listPublicApiUsage(appliedFilters)
      .then((data) => {
        if (cancelled) return
        setItems(Array.isArray(data.items) ? data.items : [])
        setSummary(formatUsageSummary(data.summary))
      })
      .catch((err) => {
        if (cancelled) return
        setError(err?.message || 'Unable to load public API usage')
        setItems([])
        setSummary(emptySummary)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [appliedFilters, refreshTick])

  const summaryCards = useMemo(() => ([
    { label: 'Requests', value: summary.requestCount },
    { label: 'Accepted', value: summary.acceptedCount },
    { label: 'Rejected', value: summary.rejectedCount },
    { label: 'Cost units', value: summary.costUnits },
  ]), [summary])

  const applyFilters = () => {
    if (!isDateRangeValid(filters.from, filters.to)) {
      setFormError('From date must be before to date.')
      return
    }
    setFormError('')
    setAppliedFilters({ ...filters })
  }

  async function confirmDeleteBucket() {
    if (!pendingDeleteBucket) return
    setError('')
    try {
      await deletePublicApiUsageBucket(pendingDeleteBucket.id)
      setPendingDeleteBucket(null)
      setRefreshTick((value) => value + 1)
    } catch (requestError) {
      setError(requestError?.message || 'Unable to delete public API usage bucket')
    }
  }

  async function confirmClearAllUsage() {
    setError('')
    try {
      await clearPublicApiUsage()
      setPendingClearAll(false)
      setRefreshTick((value) => value + 1)
    } catch (requestError) {
      setError(requestError?.message || 'Unable to clear public API usage')
    }
  }

  if (loading) {
    return (
      <PageShell>
        <LoadingState text="Loading public API usage..." />
      </PageShell>
    )
  }

  if (error) {
    return (
      <PageShell>
        <ErrorState text={error} action={<Button onClick={() => setRefreshTick((value) => value + 1)}>Retry</Button>} />
      </PageShell>
    )
  }

  return (
    <PageShell className="space-y-4">
      <PageHeader
        title="Public API Usage"
        subtitle="Inspect usage buckets, accepted and rejected counters, and cost units across public recognition endpoints."
        actions={(
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => setRefreshTick((value) => value + 1)}>Refresh</Button>
            <Button variant="danger" onClick={() => setPendingClearAll(true)}>Clear all usage</Button>
          </div>
        )}
      />

      <SectionCard title="Filters" subtitle="Use date ranges with UTC dates if you want to focus on a specific bucket window.">
        <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-4">
          <select className={ui.select} value={filters.subjectType} onChange={(event) => setFilters((current) => ({ ...current, subjectType: event.target.value }))}>
            <option value="">All subject types</option>
            {SUBJECT_TYPE_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
          <Input placeholder="Subject id" value={filters.subjectId} onChange={(event) => setFilters((current) => ({ ...current, subjectId: event.target.value }))} />
          <select className={ui.select} value={filters.endpointGroup} onChange={(event) => setFilters((current) => ({ ...current, endpointGroup: event.target.value }))}>
            <option value="">All endpoint groups</option>
            {ENDPOINT_GROUP_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
          <select className={ui.select} value={filters.bucketType} onChange={(event) => setFilters((current) => ({ ...current, bucketType: event.target.value }))}>
            <option value="">All bucket types</option>
            {BUCKET_TYPE_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
          <Input type="date" value={filters.from} onChange={(event) => setFilters((current) => ({ ...current, from: event.target.value }))} />
          <Input type="date" value={filters.to} onChange={(event) => setFilters((current) => ({ ...current, to: event.target.value }))} />
          <div className="flex items-end gap-2 md:col-span-3 xl:col-span-4">
            <Button onClick={applyFilters}>Apply filters</Button>
            <Button variant="secondary" onClick={() => { setFilters(initialFilters); setAppliedFilters(initialFilters); setFormError('') }}>Reset</Button>
          </div>
        </div>
        {formError ? <p className={ui.forms.fieldError}>{formError}</p> : null}
      </SectionCard>

      <SectionCard title="Summary">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {summaryCards.map((card) => (
            <div key={card.label} className="rounded border border-outline bg-surface-elevated p-4">
              <div className="text-xs uppercase tracking-[0.12em] text-muted-foreground">{card.label}</div>
              <div className="mt-1 text-2xl font-semibold">{card.value}</div>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Usage Buckets">
        {items.length === 0 ? (
          <EmptyState title="No usage buckets" description="Try broadening the date range or clearing the subject filters." />
        ) : (
          <DataTable
            columns={[
              { key: 'subjectType', label: 'Subject type', sortable: true },
              { key: 'subject', label: 'Subject', sortable: true, sortAccessor: (row) => formatSubjectDisplay(row.subjectType, row.subjectId) },
              { key: 'endpointGroup', label: 'Endpoint group', sortable: true },
              { key: 'bucketType', label: 'Bucket type', sortable: true },
              { key: 'bucketStart', label: 'Bucket start', sortable: true, sortAccessor: (row) => row.bucketStart || '' },
              { key: 'requestCount', label: 'Requests', sortable: true, sortAccessor: (row) => Number(row.requestCount || 0) },
              { key: 'acceptedCount', label: 'Accepted', sortable: true, sortAccessor: (row) => Number(row.acceptedCount || 0) },
              { key: 'rejectedCount', label: 'Rejected', sortable: true, sortAccessor: (row) => Number(row.rejectedCount || 0) },
              { key: 'costUnits', label: 'Cost units', sortable: true, sortAccessor: (row) => Number(row.costUnits || 0) },
              { key: 'lastRequestAt', label: 'Last request', sortable: true, sortAccessor: (row) => row.lastRequestAt || '' },
              { key: 'actions', label: 'Actions' },
            ]}
            rows={items}
            renderRow={(row) => (
              <tr key={row.id}>
                <td className={ui.tableCellText}>{row.subjectType}</td>
                <td className={ui.tableCellText}>{formatSubjectDisplay(row.subjectType, row.subjectId)}</td>
                <td className={ui.tableCellText}>{row.endpointGroup}</td>
                <td className={ui.tableCellText}>{row.bucketType}</td>
                <td className={ui.tableCellText}>{formatDateTime(row.bucketStart)}</td>
                <td className={ui.tableCellText}>{row.requestCount}</td>
                <td className={ui.tableCellText}>{row.acceptedCount}</td>
                <td className={ui.tableCellText}>{row.rejectedCount}</td>
                <td className={ui.tableCellText}>{row.costUnits}</td>
                <td className={ui.tableCellText}>{formatEmptyValue(row.lastRequestAt ? formatDateTime(row.lastRequestAt) : null)}</td>
                <td className={ui.tableCell}>
                  <Button size="sm" variant="danger" onClick={() => setPendingDeleteBucket(row)}>Delete</Button>
                </td>
              </tr>
            )}
          />
        )}
      </SectionCard>

      <ConfirmDialog
        open={Boolean(pendingDeleteBucket)}
        title="Delete usage bucket"
        message={pendingDeleteBucket ? `Delete usage bucket for ${formatSubjectDisplay(pendingDeleteBucket.subjectType, pendingDeleteBucket.subjectId)} in ${pendingDeleteBucket.bucketType}?` : 'Delete selected usage bucket?'}
        confirmText="Delete bucket"
        confirmVariant="danger"
        onConfirm={confirmDeleteBucket}
        onCancel={() => setPendingDeleteBucket(null)}
      />

      <ConfirmDialog
        open={pendingClearAll}
        title="Clear all public API usage"
        message="Delete every public API usage bucket and reset the usage history shown on this page?"
        confirmText="Clear all"
        confirmVariant="danger"
        onConfirm={confirmClearAllUsage}
        onCancel={() => setPendingClearAll(false)}
      />
    </PageShell>
  )
}
